import type { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material';
import { paperSurface } from 'c-lib';

/**
 * Styles for the hourly frame grid (`EsFrameByHourWeekly`). It always sits in the hourly breakdown
 * Dialog, so anything that must be opaque (sticky header, sticky hour column) is painted the Dialog's
 * surface: in dark mode that's background.paper plus MUI's elevation overlay, not background.paper.
 *
 * These are objects, not `theme => ({...})`: ReportTableWrapper spreads body cell sx into an
 * object, which would drop a function. Theme-dependent values use per-property callbacks.
 */
export const DIALOG_ELEVATION = 24;

const dialogSurface = {
    bgcolor: (theme: Theme) => paperSurface(theme, DIALOG_ELEVATION).backgroundColor,
    backgroundImage: (theme: Theme) => paperSurface(theme, DIALOG_ELEVATION).backgroundImage,
};

export const gridCellSx = {
    border: 1,
    borderColor: 'divider',
    px: 1.25,
    py: 0.75,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
} as const;

// A heavier left rule where each week starts, down the whole column.
const weekStartSx = { borderLeftWidth: 2, borderLeftColor: 'text.disabled' } as const;

const todayTintSx = { bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08) };

export const dayCellSx = ({
    isWeekStart,
    isToday,
    isOverall = false,
}: {
    isWeekStart: boolean;
    isToday: boolean;
    isOverall?: boolean;
}): SxProps<Theme> => ({
    ...gridCellSx,
    ...(isWeekStart ? weekStartSx : {}),
    ...(isToday ? todayTintSx : {}),
    ...(isOverall ? { borderBottomWidth: 2, borderBottomColor: 'text.disabled' } : {}),
});

// The header's background comes from ReportTableWrapper's headerSurfaceElevation; a header cell's
// own background would lose to it, so today is marked with a rule and a badge instead of a tint.
export const dayHeaderSx = ({
    isWeekStart,
    isToday,
}: {
    isWeekStart: boolean;
    isToday: boolean;
}): SxProps<Theme> => ({
    ...gridCellSx,
    ...(isWeekStart ? weekStartSx : {}),
    verticalAlign: 'bottom',
    borderBottomWidth: 2,
    borderBottomColor: isToday ? 'primary.main' : 'divider',
});

export const cornerHeaderSx: SxProps<Theme> = {
    ...gridCellSx,
    textAlign: 'left',
    verticalAlign: 'bottom',
    borderBottomWidth: 2,
    // Sticky both ways: above the day headers and the hour column when scrolling.
    left: 0,
    zIndex: 3,
};

export const hourCellSx = ({
    isOverall = false,
}: { isOverall?: boolean } = {}): SxProps<Theme> => ({
    ...gridCellSx,
    ...dialogSurface,
    ...(isOverall ? { borderBottomWidth: 2, borderBottomColor: 'text.disabled' } : {}),
    textAlign: 'left',
    position: 'sticky',
    left: 0,
    zIndex: 1,
});

export const todayBadgeSx = (theme: Theme) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: 1,
    px: 0.75,
    lineHeight: 1.6,
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
});
